import { normalizeAppBaseUrl } from './auth-cross-app.handoff';

/** Página estática que limpia localStorage del origen destino. */
export const CROSS_APP_LOGOUT_PATH = '/sso-logout.html';

/** Hash opcional (`#ap_logout=1`) si se aterriza en el SPA. */
export const CROSS_APP_LOGOUT_HASH_KEY = 'ap_logout';

const LOCAL_DEV_REPO_PORTS = [4201, 4202, 4203, 4204, 4205];

export function buildCrossAppLogoutUrl(targetBaseUrl: string): string {
  const base = normalizeAppBaseUrl(targetBaseUrl);
  try {
    return `${new URL(base).origin}${CROSS_APP_LOGOUT_PATH}`;
  } catch {
    return `${base}${CROSS_APP_LOGOUT_PATH}`;
  }
}

/** Orígenes a notificar: catálogo API + puertos locales conocidos. */
export function collectCrossAppLogoutOrigins(repoCodes: string[]): string[] {
  const origins = new Set<string>();

  for (const code of repoCodes) {
    try {
      origins.add(new URL(code).origin);
    } catch {
      // ignore invalid URLs
    }
  }

  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    for (const port of LOCAL_DEV_REPO_PORTS) {
      origins.add(`http://localhost:${port}`);
    }
  }

  return [...origins];
}

/**
 * Carga un iframe oculto en cada origen para borrar su localStorage.
 * No usar sandbox: sin allow-same-origin no se limpia el storage real.
 */
export function broadcastCrossAppLogout(origins: string[]): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const current = window.location.origin;
  const unique = [...new Set(origins)].filter(
    (origin) => origin && origin !== current,
  );

  for (const origin of unique) {
    const iframe = document.createElement('iframe');
    iframe.src = buildCrossAppLogoutUrl(origin);
    iframe.setAttribute('aria-hidden', 'true');
    iframe.tabIndex = -1;
    iframe.style.cssText =
      'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none;left:-9999px;top:0';
    document.body.appendChild(iframe);

    const remove = (): void => {
      iframe.remove();
    };
    iframe.addEventListener('load', () => window.setTimeout(remove, 1500));
    iframe.addEventListener('error', remove);
    window.setTimeout(remove, 8000);
  }
}

/** Lee `#ap_logout` y limpia el hash. */
export function readCrossAppLogoutFromLocation(
  loc: Location = window.location,
): boolean {
  if (typeof loc === 'undefined') return false;

  const rawHash = (loc.hash ?? '').replace(/^#/, '');
  if (!rawHash) return false;

  const params = new URLSearchParams(rawHash);
  if (!params.has(CROSS_APP_LOGOUT_HASH_KEY)) return false;

  try {
    window.history.replaceState(null, '', `${loc.pathname}${loc.search}`);
  } catch {
    // ignore
  }
  return true;
}
